import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Maximize2, Minimize2, Presentation } from 'lucide-react';
import { fetchChapterSlides } from '@/lib/chapterDigest';
import useActiveScope from '@/lib/useActiveScope';
import SlideVisual from '@/components/study/SlideVisual';

/** Slides — one slide per topic/sub-topic, built once per chapter and cached. */
export default function SlidesPage() {
  const { subject, topic } = useActiveScope();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);

  const load = useCallback(async () => {
    if (!subject || !topic) return;
    setError('');
    try { setData(await fetchChapterSlides(subject, topic)); setIdx(0); }
    catch (e) { setError(e?.message || 'The slides are unavailable right now'); }
  }, [subject, topic]);

  useEffect(() => { load(); }, [load]);

  const slides = useMemo(() => {
    const base = data?.payload?.slides || [];
    const art = (data?.gallery || []).filter(g => g.file_url);
    if (!base.length || !art.length) return base;
    return [...base.slice(0, 1), { kind: 'gallery', art }, ...base.slice(1)];
  }, [data]);
  const go = useCallback((delta) => setIdx(i => Math.min(slides.length - 1, Math.max(0, i + delta))), [slides.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'Escape') setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (!subject || !topic) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="slides-no-scope">
        <Presentation className="w-10 h-10 text-fuchsia-300 mx-auto" />
        <p className="font-heading text-base text-slate-800 mt-3">Pick a subject and chapter first</p>
        <p className="font-body text-sm text-slate-500 mt-1">Choose them in the bar at the top and your deck appears here.</p>
      </div>
    );
  }

  const slide = slides[idx];
  const deck = (
    <div className={full ? 'fixed inset-0 z-[70] bg-slate-950 p-4 sm:p-10 flex flex-col' : ''} data-testid="slides-deck">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className={`font-heading text-[11px] uppercase tracking-widest ${full ? 'text-slate-400' : 'text-slate-400'}`}>
          {data?.payload?.title || topic} · slide {slides.length ? idx + 1 : 0} of {slides.length}
        </p>
        <button onClick={() => setFull(f => !f)} data-testid="slides-fullscreen-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-heading transition-colors ${
            full ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}>
          {full ? <><Minimize2 className="w-3.5 h-3.5" /> Exit full screen</> : <><Maximize2 className="w-3.5 h-3.5" /> Full screen</>}
        </button>
      </div>

      {slide ? (
        <div className={`relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-3xl border flex flex-col justify-center ${full ? 'border-white/10' : 'border-slate-200'} bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-600 text-white p-6 sm:p-10`}
          data-testid={`slide-${idx}`}>
          <div className="absolute -top-16 -right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
          {slide.kind === 'gallery' ? (
            <div className="relative" data-testid="slides-gallery">
              <p className="font-heading text-[11px] uppercase tracking-[0.2em] text-white/70">{subject}</p>
              <h2 className={`font-display font-bold tracking-tight mt-2 ${full ? 'text-4xl sm:text-5xl' : 'text-2xl sm:text-4xl'}`}>
                Picture this chapter
              </h2>
              <p className="font-body text-white/80 text-[12px] sm:text-sm mt-1">
                Art our teachers made for this chapter — look, then read on.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                {slide.art.slice(0, 4).map((a, i) => (
                  <figure key={a.content_id} className="rounded-2xl overflow-hidden bg-white/10 border border-white/20 opacity-0 animate-slide-up"
                          style={{ animationDelay: `${120 + i * 110}ms` }}>
                    <img src={a.thumbnail_url || a.file_url} alt={a.title}
                         className={`w-full object-cover ${full ? 'h-64' : 'h-44'}`} loading="lazy" />
                    <figcaption className="px-3 py-2">
                      <p className="font-heading text-[12px] text-white">{a.title}</p>
                      {a.description && <p className="font-body text-[10px] text-white/70 leading-snug">{a.description}</p>}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          ) : (
          <div className={`relative grid gap-6 sm:gap-8 ${slide.visual ? 'lg:grid-cols-[1.05fr,1fr] lg:items-start' : 'max-w-3xl'}`}>
            <div className="min-w-0">
              <p className="font-heading text-[11px] uppercase tracking-[0.2em] text-white/70">{subject}</p>
              <h2 className={`font-display font-bold tracking-tight mt-2 leading-tight ${full ? 'text-3xl sm:text-5xl' : 'text-2xl sm:text-4xl'}`}>
                {slide.title}
              </h2>
              {slide.subtitle && <p className="font-body text-white/85 mt-2 text-sm sm:text-base">{slide.subtitle}</p>}
              <ul className="mt-5 space-y-2.5">
                {(slide.bullets || []).map((b, i) => (
                  <li key={i} style={{ animationDelay: `${80 + i * 90}ms` }}
                      className={`flex items-start gap-2.5 font-body text-white/95 opacity-0 animate-slide-up ${full ? 'text-base sm:text-lg' : 'text-[14px] sm:text-[16px]'}`}>
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              {slide.takeaway && (
                <p className="mt-6 inline-block rounded-2xl bg-white/15 backdrop-blur-sm px-4 py-2 font-heading text-[12px] sm:text-sm">
                  Remember: {String(slide.takeaway).replace(/^\s*remember\s*[:\u2014-]\s*/i, '')}
                </p>
              )}
            </div>
            {slide.visual && (
              <div className="min-w-0 lg:border-l lg:border-white/15 lg:pl-8 self-start">
                <SlideVisual visual={slide.visual} replayKey={idx} />
              </div>
            )}
          </div>
          )}
        </div>
      ) : (
        <p className="flex items-center gap-2 text-slate-400 font-body text-sm" data-testid="slides-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Building your deck…
        </p>
      )}

      <div className="flex items-center justify-between gap-3 mt-4">
        <button onClick={() => go(-1)} disabled={idx === 0} data-testid="slides-prev"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-heading disabled:opacity-40 ${
            full ? 'bg-white/10 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`slides-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-fuchsia-500' : 'w-1.5 bg-slate-300'}`} />
          ))}
        </div>
        <button onClick={() => go(1)} disabled={idx >= slides.length - 1} data-testid="slides-next"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-[12px] font-heading disabled:opacity-40">
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="slides-page">
      <div className="mb-5">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Slides</h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          {subject} · {topic} — flip through the chapter, one idea per slide.
        </p>
      </div>
      {error && !slides.length && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4" data-testid="slides-error">
          <p className="font-body text-sm text-rose-700">{error}</p>
          <button onClick={load} className="mt-2 text-sm font-heading text-rose-700 underline" data-testid="slides-retry">Try again</button>
        </div>
      )}
      {deck}
    </div>
  );
}
